// Craft menu — the crafting "recipe book" (opened with U / the HUD button) and the station
// processing panel (opened by clicking a crafting machine in the world).
//
//  - BOOK mode: lists every recipe under each profession the player has LEARNED (professions they
//    don't have are hidden). Selecting a recipe shows its inputs/output and the live success rate
//    at the player's current profession level. View-only — it tells you to use the matching station.
//  - STATION mode: at a machine, lists only the recipes that machine processes which the player can
//    craft, adds a quantity stepper, and a Process button that starts the 10s crafting channel.
//
// The look matches the minimal black theme (theme.ts vars), like the ATM/shop overlays.

import {
  CRAFT_RECIPES, recipesForStation, recipeById, recipesForProfession,
  craftSuccessRate, PROFESSION_NAMES, PROFESSIONS, ITEM_NAMES,
  CRAFT_STATION_NAMES, professionLevelCap, gearStat, LEATHER_FAMILIES,
  type CraftRecipe, type CraftStation,
} from "@ghostland/shared";
import { injectTheme } from "./theme.js";
import { statsStore } from "../items/statsStore.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { drawItemIcon } from "../items/itemArt.js";

export interface CraftActions {
  /** Start processing `quantity` units of `recipeId` at the current station (server-authorised). */
  process: (recipeId: string, quantity: number) => void;
}
let actions: CraftActions | null = null;
export function setCraftActions(a: CraftActions): void { actions = a; }

const STYLE_ID = "ghostland-craft-style";
const CSS = `
#gl-craft-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(5,0,7,.55);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-craft-overlay.open { display: flex; }
#gl-craft-card {
  width: 560px; max-width: 92vw; max-height: 82vh; display: flex; flex-direction: column;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 16px 50px rgba(0,0,0,.7);
}
#gl-craft-head { padding: 18px 20px 12px; border-bottom: 1px solid var(--gl-border); }
#gl-craft-head h1 { margin: 0; font-size: 16px; letter-spacing: .18em; }
#gl-craft-head .hint { font-size: 11px; color: var(--gl-muted); margin-top: 4px; }
#gl-craft-body { display: flex; min-height: 0; flex: 1; }
#gl-craft-list { width: 220px; overflow-y: auto; padding: 10px; border-right: 1px solid var(--gl-border); }
#gl-craft-list .prof { font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--gl-muted); margin: 10px 6px 6px; }
#gl-craft-list .prof:first-child { margin-top: 2px; }
.gl-craft-row {
  display: flex; align-items: center; gap: 8px; padding: 7px 8px; border-radius: 7px; cursor: pointer;
  border: 1px solid transparent; font-size: 12px;
}
.gl-craft-row:hover { background: var(--gl-panel-2); }
.gl-craft-row.sel { background: var(--gl-panel-2); border-color: var(--gl-border-focus); }
.gl-craft-row.locked { opacity: .5; cursor: default; }
.gl-craft-row canvas { width: 26px; height: 26px; image-rendering: pixelated; }
#gl-craft-empty { color: var(--gl-muted); font-size: 12px; padding: 12px; }
#gl-craft-detail { flex: 1; padding: 16px 18px; overflow-y: auto; }
#gl-craft-detail h2 { margin: 0 0 2px; font-size: 15px; }
#gl-craft-detail .sub { font-size: 11px; color: var(--gl-muted); margin-bottom: 14px; }
.gl-craft-io { display: flex; align-items: center; gap: 10px; margin: 10px 0; }
.gl-craft-io .chip { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.gl-craft-io .chip canvas { width: 28px; height: 28px; image-rendering: pixelated; }
.gl-craft-io .chip .lack { color: #e06a6a; }
.gl-craft-io .arrow { color: var(--gl-muted); }
.gl-craft-rate { font-size: 13px; margin: 12px 0; }
.gl-craft-rate b { color: var(--gl-selected); }
#gl-craft-foot { padding: 12px 18px 16px; border-top: 1px solid var(--gl-border); }
.gl-craft-qty { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 12px; }
.gl-craft-qty input {
  width: 70px; padding: 7px 8px; box-sizing: border-box; text-align: center;
  background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 6px;
  color: var(--gl-text); font-family: inherit; font-size: 13px;
}
.gl-craft-qty button {
  width: 28px; height: 28px; background: var(--gl-panel-2); border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-text); cursor: pointer; font-family: inherit; font-size: 14px;
}
.gl-craft-qty button:hover { border-color: var(--gl-border-focus); }
.gl-craft-qty .max { color: var(--gl-muted); }
#gl-craft-go {
  width: 100%; padding: 10px 0; background: var(--gl-accent); color: var(--gl-accent-fg);
  border: 1px solid var(--gl-accent); border-radius: 7px; cursor: pointer; font-family: inherit; font-size: 13px;
}
#gl-craft-go:hover:not(:disabled) { background: var(--gl-accent-hover); border-color: var(--gl-accent-hover); }
#gl-craft-go:disabled { opacity: .45; cursor: default; }
#gl-craft-note { font-size: 11px; color: var(--gl-muted); margin-top: 10px; text-align: center; }
#gl-craft-close { margin-top: 10px; width: 100%; padding: 7px 0; background: none;
  border: 1px solid var(--gl-border); border-radius: 6px; color: var(--gl-muted); cursor: pointer; font-family: inherit; font-size: 11px; }
`;

let overlay: HTMLDivElement | null = null;
let listEl: HTMLDivElement | null = null;
let detailEl: HTMLDivElement | null = null;
let footEl: HTMLDivElement | null = null;
let unsub: Array<() => void> = [];

/** Current mode + selection. `station` null ⇒ book mode (view-only). */
let station: CraftStation | null = null;
let selectedId: string | null = null;

function ownedCount(itemId: string): number {
  return inventoryStore.get().reduce((n, s) => (s.itemId === itemId ? n + s.quantity : n), 0);
}
/** How many units of a recipe the player can afford from the bag right now. */
function affordable(recipe: CraftRecipe): number {
  let min = Infinity;
  for (const ing of recipe.inputs) min = Math.min(min, Math.floor(ownedCount(ing.itemId) / ing.qty));
  // Upgrade recipes also consume one piece from the leather family per unit.
  if (recipe.upgrade) {
    const fam = LEATHER_FAMILIES[recipe.upgrade.family].reduce((n, id) => n + ownedCount(id), 0);
    min = Math.min(min, fam);
  }
  return Number.isFinite(min) ? min : 0;
}
/** Recipes shown for the current mode: book = all professions you have; station = that station's. */
function visibleRecipes(): CraftRecipe[] {
  if (station) {
    return recipesForStation(station).filter((r) => statsStore.get().professions.some((p) => p.id === r.profession));
  }
  const have = new Set(statsStore.get().professions.map((p) => p.id));
  return CRAFT_RECIPES.filter((r) => have.has(r.profession));
}

function iconFor(itemId: string): HTMLCanvasElement {
  const c = drawItemIcon(itemId);
  c.style.imageRendering = "pixelated";
  return c;
}

function renderList(): void {
  if (!listEl) return;
  listEl.innerHTML = "";
  const recipes = visibleRecipes();
  if (recipes.length === 0) {
    const empty = document.createElement("div");
    empty.id = "gl-craft-empty";
    empty.textContent = station
      ? "You don't know a profession that uses this machine."
      : "You haven't learned any professions yet. Find a trainer in the city.";
    listEl.appendChild(empty);
    selectedId = null;
    renderDetail();
    return;
  }
  // Group by profession (in PROFESSIONS order) so the book reads cleanly.
  if (!selectedId || !recipes.some((r) => r.id === selectedId)) selectedId = recipes[0].id;
  for (const prof of PROFESSIONS) {
    const group = recipes.filter((r) => r.profession === prof);
    if (group.length === 0) continue;
    const level = statsStore.professionLevel(prof);
    const head = document.createElement("div");
    head.className = "prof";
    head.textContent = `${PROFESSION_NAMES[prof]} · Lv ${level}`;
    listEl.appendChild(head);
    for (const r of group) {
      const locked = level < r.unlockLevel;
      const row = document.createElement("div");
      row.className = "gl-craft-row" + (r.id === selectedId ? " sel" : "") + (locked ? " locked" : "");
      row.appendChild(iconFor(r.output.itemId));
      const label = document.createElement("span");
      label.textContent = locked ? `${r.name} (Lv ${r.unlockLevel})` : r.name;
      row.appendChild(label);
      row.addEventListener("click", () => { selectedId = r.id; renderList(); });
      listEl.appendChild(row);
    }
  }
  renderDetail();
}

const STAT_LABEL: Record<string, string> = { strength: "STR", dex: "DEX", agility: "AGI" };
/** A note describing a recipe's output gear bonuses (armor + the rolled/fixed stat). "" if not gear. */
function gearNote(recipe: CraftRecipe): string {
  const ids = recipe.rollVariants && recipe.rollVariants.length ? recipe.rollVariants : [recipe.output.itemId];
  const stats = ids.map((id) => gearStat(id)).filter((g): g is NonNullable<typeof g> => !!g);
  if (stats.length === 0) return "";
  const armor = stats[0].armor;
  // Armored upgrade: keeps the consumed piece's base stat + rolls extra random points.
  if (recipe.upgrade) {
    return `<div class="gl-craft-rate">Yields: <b>+${armor} Armor</b> · keeps the piece's <b>+1 stat</b> · <b>+${recipe.rollMods ?? 0} random points</b> <span style="color:var(--gl-muted)">(STR/DEX/AGI)</span></div>`;
  }
  const rolled = [...new Set(stats.map((g) => STAT_LABEL[g.stat] ?? g.stat))];
  const statTxt = recipe.rollVariants && rolled.length > 1
    ? `+${stats[0].amount} random: ${rolled.join(" / ")}`
    : `+${stats[0].amount} ${rolled[0]}`;
  return `<div class="gl-craft-rate">Yields: <b>+${armor} Armor</b> · <b>${statTxt}</b></div>`;
}

function renderDetail(): void {
  if (!detailEl || !footEl) return;
  const recipe = selectedId ? recipeById(selectedId) : undefined;
  if (!recipe) { detailEl.innerHTML = ""; footEl.innerHTML = ""; return; }
  const level = statsStore.professionLevel(recipe.profession);
  const rate = craftSuccessRate(recipe, Math.max(1, level));

  detailEl.innerHTML =
    `<h2>${recipe.name}</h2>` +
    `<div class="sub">${PROFESSION_NAMES[recipe.profession]} · requires Lv ${recipe.unlockLevel} · ${CRAFT_STATION_NAMES[recipe.station]}</div>` +
    `<div class="gl-craft-io" id="gl-craft-io"></div>` +
    `<div class="gl-craft-rate">Success rate: <b>${rate}%</b>${level === 0 ? " <span style='color:var(--gl-muted)'>(learn the profession first)</span>" : level >= professionLevelCap(recipe.profession) ? " (max level)" : ""}</div>` +
    gearNote(recipe);

  // Build the input → output row with real pixel icons.
  const io = detailEl.querySelector("#gl-craft-io")!;
  let first = true;
  const sep = (): void => { if (!first) { const p = document.createElement("span"); p.className = "arrow"; p.textContent = "+"; io.appendChild(p); } first = false; };
  // Upgrade recipes also consume one piece of a normal-leather family ("Any Leather Top/Pants").
  if (recipe.upgrade) {
    const fam = LEATHER_FAMILIES[recipe.upgrade.family];
    const haveFam = fam.reduce((n, id) => n + ownedCount(id), 0);
    sep();
    const chip = document.createElement("div"); chip.className = "chip";
    chip.appendChild(iconFor(fam[0]));
    const lab = document.createElement("span"); lab.className = haveFam < 1 ? "lack" : "";
    lab.textContent = `Any ${recipe.upgrade.family === "leather_top" ? "Leather Top" : "Leather Pants"} ${haveFam}/1`;
    chip.appendChild(lab); io.appendChild(chip);
  }
  recipe.inputs.forEach((ing) => {
    sep();
    const chip = document.createElement("div"); chip.className = "chip";
    chip.appendChild(iconFor(ing.itemId));
    const have = ownedCount(ing.itemId);
    const lab = document.createElement("span"); lab.className = have < ing.qty ? "lack" : "";
    lab.textContent = `${ITEM_NAMES[ing.itemId as keyof typeof ITEM_NAMES] ?? ing.itemId} ${have}/${ing.qty}`;
    chip.appendChild(lab); io.appendChild(chip);
  });
  const arrow = document.createElement("span"); arrow.className = "arrow"; arrow.textContent = "→"; io.appendChild(arrow);
  const outChip = document.createElement("div"); outChip.className = "chip";
  outChip.appendChild(iconFor(recipe.output.itemId));
  const outLab = document.createElement("span");
  outLab.textContent = `${ITEM_NAMES[recipe.output.itemId as keyof typeof ITEM_NAMES] ?? recipe.output.itemId}${recipe.output.qty > 1 ? ` ×${recipe.output.qty}` : ""}`;
  outChip.appendChild(outLab); io.appendChild(outChip);

  renderFoot(recipe, level);
}

function renderFoot(recipe: CraftRecipe, level: number): void {
  if (!footEl) return;
  // BOOK mode: just a note telling the player where to craft.
  if (!station) {
    footEl.innerHTML = `<div id="gl-craft-note">Use a ${CRAFT_STATION_NAMES[recipe.station]} to craft this.</div>`;
    return;
  }
  const can = level >= recipe.unlockLevel;
  const max = affordable(recipe);
  footEl.innerHTML =
    `<div class="gl-craft-qty">Quantity
      <button id="gl-craft-minus">−</button>
      <input id="gl-craft-q" type="number" min="1" value="1" />
      <button id="gl-craft-plus">+</button>
      <span class="max">/ ${max} max</span>
    </div>
    <button id="gl-craft-go">Process</button>`;
  const input = footEl.querySelector<HTMLInputElement>("#gl-craft-q")!;
  const go = footEl.querySelector<HTMLButtonElement>("#gl-craft-go")!;
  const clamp = (): number => {
    let v = Math.floor(Number(input.value) || 1);
    v = Math.max(1, Math.min(Math.max(1, max), v));
    input.value = String(v);
    return v;
  };
  footEl.querySelector("#gl-craft-minus")!.addEventListener("click", () => { input.value = String(Math.max(1, (Math.floor(Number(input.value) || 1)) - 1)); clamp(); });
  footEl.querySelector("#gl-craft-plus")!.addEventListener("click", () => { input.value = String((Math.floor(Number(input.value) || 1)) + 1); clamp(); });
  input.addEventListener("change", clamp);
  if (!can || max < 1) {
    go.disabled = true;
    go.textContent = !can ? `Requires Level ${recipe.unlockLevel}` : "Not enough materials";
  }
  go.addEventListener("click", () => {
    if (go.disabled) return;
    const qty = clamp();
    actions?.process(recipe.id, qty);
    closeCraft();
  });
}

function refresh(): void { renderList(); }

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-craft-overlay";
  overlay.innerHTML = `
    <div id="gl-craft-card">
      <div id="gl-craft-head"><h1>CRAFT</h1><div class="hint" id="gl-craft-sub"></div></div>
      <div id="gl-craft-body">
        <div id="gl-craft-list"></div>
        <div id="gl-craft-detail"></div>
      </div>
      <div id="gl-craft-foot"></div>
      <div style="padding:0 18px 16px"><button id="gl-craft-close">Close (Esc)</button></div>
    </div>`;
  document.body.appendChild(overlay);
  listEl = overlay.querySelector("#gl-craft-list");
  detailEl = overlay.querySelector("#gl-craft-detail");
  footEl = overlay.querySelector("#gl-craft-foot");
  overlay.querySelector("#gl-craft-close")!.addEventListener("click", closeCraft);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeCraft(); });
}

function open(): void {
  ensure();
  const sub = overlay!.querySelector("#gl-craft-sub")!;
  const head = overlay!.querySelector<HTMLHeadingElement>("#gl-craft-head h1")!;
  if (station) {
    head.textContent = CRAFT_STATION_NAMES[station].toUpperCase();
    sub.textContent = "Pick a recipe, set a quantity, then Process. Stand still while it runs.";
  } else {
    head.textContent = "CRAFTING";
    sub.textContent = "Recipes for the professions you've learned.";
  }
  refresh();
  unsub = [statsStore.subscribe(refresh), inventoryStore.subscribe(refresh)];
  overlay!.classList.add("open");
}

/** Open the recipe book (U key / HUD button) — view-only across all learned professions. */
export function openCraftBook(): void { station = null; selectedId = null; open(); }

/** Open the station panel at a machine — recipes that machine processes, with a Process button. */
export function openCraftStation(s: CraftStation): void { station = s; selectedId = null; open(); }

export function closeCraft(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}

export function isCraftOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}

/** True if the player has learned any profession with at least one recipe (drives whether the U
 *  menu has anything to show — used only for messaging). */
export function hasAnyCraftRecipe(): boolean {
  return statsStore.get().professions.some((p) => recipesForProfession(p.id).length > 0);
}
